import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { View, Text, TouchableOpacity, FlatList, StyleSheet, ActivityIndicator, TextInput, ScrollView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from '../../components/icons/AppIcon';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { ScreenLayout, Header } from '../../components';
import { createTabPressHandler } from '../../navigation/handleTabPress';
import { InsetModal as Modal } from '../../components/InsetModal';
import { overlayPickerStyles } from '../../components/OverlayPanel';
import { useAppContext } from '../../contexts/AppContext';
import { useAppStore } from '../../store/appStore';
import {
  apiService,
  FlowRunItem,
  FlowRunDetail,
  FlowRunEffect,
  FlowRunStep,
  FlowRunSkip,
  FlowRunStatusFilter,
} from '../../services/api';
import { useFlowPermissions } from '../../hooks/useFlowPermissions';
import { NODE_TYPE_META } from '../../components/flows/flow-types';
import { FlowRunStepPicker } from '../../components/flows/FlowRunStepPicker';
import { colors, typography, withAlpha } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/**
 * Dashboard → Flow runs: every run of every flow, the way a data list's sync runs are shown —
 * filter by flow, status, contact, a user it assigned (or who stopped / resumed it), any record
 * id it touched, and date; open a run for its step-by-step timeline, the records it created /
 * changed / sent, errors and saved values; plus the "Skipped" starts with their reason.
 * Opened with routeParams.flowId (Flow editor → Runs) it starts filtered to that flow.
 */

const LIMIT = 25;
const CHANNEL_ICON: Record<string, string> = { whatsapp: 'logo-whatsapp', facebook_messenger: 'logo-facebook', x_twitter: 'logo-twitter' };

type Tab = FlowRunStatusFilter | 'skipped';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Running' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'paused', label: 'Paused' },
  { id: 'errors', label: 'With errors' },
  { id: 'completed', label: 'Completed' },
  { id: 'stopped', label: 'Stopped' },
  { id: 'skipped', label: 'Skipped' },
];
const RANGES: Array<{ id: string; label: string; days?: number }> = [
  { id: 'any', label: 'Any time' },
  { id: '1', label: 'Today', days: 1 },
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
];
const ENDED_REASON: Record<string, string> = {
  timeout: 'contact went silent',
  agent_reply: 'a team member replied',
  manual: 'stopped by a team member',
  flow_missing: 'the flow was turned off',
};
const SKIP_REASON: Record<string, string> = {
  flow_inactive: 'The flow is turned off',
  not_published: 'The flow has not been published',
  already_running: 'The contact was already in this flow',
  contact_assigned: 'The contact is assigned to a team member (auto-reply flows skip assigned contacts)',
};
const TRIGGER: Record<string, string> = {
  reply_item: 'Quick reply',
  dataset: 'Dataset',
  channel_settings: 'Channel auto-reply',
  call_flow: 'Called by flow',
  ai_agent: 'AI agent',
  assignment_rule: 'Assignment rule',
};
const EFFECT_KIND: Record<FlowRunEffect['kind'], { label: string; icon: string }> = {
  message: { label: 'Messages', icon: 'chatbubble-outline' },
  contact: { label: 'Contact', icon: 'person-outline' },
  custom_fields: { label: 'Custom fields', icon: 'list-outline' },
  task: { label: 'Tasks', icon: 'checkbox-outline' },
  assignment: { label: 'Assignments', icon: 'person-add-outline' },
  calendar_event: { label: 'Bookings', icon: 'calendar-outline' },
  data_list_record: { label: 'Data list rows', icon: 'server-outline' },
  webhook: { label: 'Webhooks', icon: 'globe-outline' },
  post: { label: 'Posts', icon: 'logo-facebook' },
  otp: { label: 'Codes', icon: 'key-outline' },
  dataset: { label: 'Datasets', icon: 'layers-outline' },
  order: { label: 'Orders', icon: 'receipt-outline' },
  quotation: { label: 'Quotations', icon: 'document-text-outline' },
  vendor: { label: 'Vendors', icon: 'storefront-outline' },
  notification: { label: 'Team alerts', icon: 'notifications-outline' },
};

const statusMeta = (status: string) => ({
  active: { icon: 'sync-circle', color: colors.primary as string, label: 'Running' },
  waiting: { icon: 'hourglass', color: colors.warning as string, label: 'Waiting' },
  paused: { icon: 'pause-circle', color: colors.warning as string, label: 'Paused' },
  completed: { icon: 'checkmark-circle', color: colors.status.success as string, label: 'Completed' },
  stopped: { icon: 'stop-circle', color: colors.text.secondary as string, label: 'Stopped' },
}[status] || { icon: 'ellipse-outline', color: colors.text.secondary as string, label: status });

const outcomeMeta = (outcome: FlowRunStep['outcome']) => ({
  done: { icon: 'checkmark-circle', color: colors.status.success as string },
  error: { icon: 'close-circle', color: colors.error as string },
  waiting: { icon: 'hourglass', color: colors.warning as string },
  paused: { icon: 'pause-circle', color: colors.warning as string },
  called: { icon: 'git-pull-request', color: colors.primary as string },
  ended: { icon: 'flag', color: colors.text.secondary as string },
  ignored: { icon: 'remove-circle', color: colors.text.secondary as string },
}[outcome] || { icon: 'ellipse', color: colors.text.secondary as string });

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' } as any) : '—');
const formatTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const formatDuration = (ms?: number) => {
  if (ms === undefined || ms < 0) return '';
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${hours}h ${minutes % 60}m` : `${Math.floor(hours / 24)}d ${hours % 24}h`;
};
/** Step details carry ISO times ("Waiting until 2026-10-18T06:00:00.000Z") — shown local. */
const localizeDetail = (text?: string) => (text || '').replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g, (iso) => formatDate(iso));

interface Option { id: string; label: string; hint?: string }

/** A filter that opens a searchable list. */
const SelectFilter: React.FC<{ icon: string; placeholder: string; value?: string; options: Option[]; onChange: (id?: string) => void }> = ({ icon, placeholder, value, options, onChange }) => {
  const styles = useThemedStyles(getStyles);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.find((o) => o.id === value);
  const shown = options.filter((o) => !search.trim() || o.label.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 200);
  return (
    <>
      <TouchableOpacity style={[styles.filterPill, !!value && styles.filterPillOn]} onPress={() => { setSearch(''); setOpen(true); }}>
        <Ionicons name={icon as any} size={14} color={value ? (colors.primary as string) : (colors.text.secondary as string)} />
        <Text style={[styles.filterPillText, !!value && styles.filterPillTextOn]} numberOfLines={1}>{selected?.label || placeholder}</Text>
        {value ? (
          <TouchableOpacity accessibilityLabel={`Clear ${placeholder}`} hitSlop={8} onPress={() => onChange(undefined)}>
            <Ionicons name="close" size={14} color={colors.primary as string} />
          </TouchableOpacity>
        ) : (
          <Ionicons name="chevron-down" size={14} color={colors.text.secondary as string} />
        )}
      </TouchableOpacity>
      {open && (
        <Modal visible transparent animationType="fade" onRequestClose={() => setOpen(false)}>
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setOpen(false)}>
            <TouchableOpacity activeOpacity={1} style={styles.modalCard}>
              <Text style={styles.modalTitle}>{placeholder}</Text>
              <TextInput style={styles.searchInput} value={search} onChangeText={setSearch} placeholder="Search" placeholderTextColor={colors.text.secondary as string} autoFocus />
              <ScrollView style={{ maxHeight: 360 }}>
                {shown.map((o) => (
                  <TouchableOpacity key={o.id} style={styles.optionRow} onPress={() => { onChange(o.id); setOpen(false); }}>
                    <Text style={[styles.optionText, o.id === value && { color: colors.primary as string }]} numberOfLines={1}>{o.label}</Text>
                    {!!o.hint && <Text style={styles.optionHint} numberOfLines={1}>{o.hint}</Text>}
                  </TouchableOpacity>
                ))}
                {shown.length === 0 && <Text style={styles.hint}>Nothing found</Text>}
              </ScrollView>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

/** A text filter applied after a short pause in typing. */
const SearchFilter: React.FC<{ icon: string; placeholder: string; value: string; onChange: (text: string) => void; width?: number }> = ({ icon, placeholder, value, onChange, width = 200 }) => {
  const styles = useThemedStyles(getStyles);
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => setText(value), [value]);
  const update = (next: string) => {
    setText(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(next.trim()), 400);
  };
  return (
    <View style={[styles.filterPill, { width }, !!value && styles.filterPillOn]}>
      <Ionicons name={icon as any} size={14} color={value ? (colors.primary as string) : (colors.text.secondary as string)} />
      <TextInput style={styles.filterInput} value={text} onChangeText={update} placeholder={placeholder} placeholderTextColor={colors.text.secondary as string} />
      {!!text && (
        <TouchableOpacity accessibilityLabel={`Clear ${placeholder}`} hitSlop={8} onPress={() => { setText(''); onChange(''); }}>
          <Ionicons name="close" size={14} color={colors.primary as string} />
        </TouchableOpacity>
      )}
    </View>
  );
};

export const DashboardFlowsAllScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const { canEdit } = useFlowPermissions();

  const [tab, setTab] = useState<Tab>('all');
  const [flowId, setFlowId] = useState<string | undefined>((routeParams as any)?.flowId);
  const [userId, setUserId] = useState<string | undefined>((routeParams as any)?.userId);
  const [departmentId, setDepartmentId] = useState<string | undefined>((routeParams as any)?.departmentId);
  const [contact, setContact] = useState('');
  const [ref, setRef] = useState<string>((routeParams as any)?.ref || '');
  const [range, setRange] = useState('any');

  const [flows, setFlows] = useState<Option[]>([]);
  const [users, setUsers] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);
  const [runs, setRuns] = useState<FlowRunItem[]>([]);
  const [skips, setSkips] = useState<FlowRunSkip[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    apiService.getFlows({ limit: 1000, skip: 0 }).then((res) => {
      if (res.statusCode !== 1000) return;
      const own: Option[] = (res.data?.items || []).map((f: any) => ({ id: f.id || f._id, label: f.name, hint: f.isActive === false ? 'Off' : undefined }));
      setFlows(own);
      // AI agents' conversations run in their own (hidden) flows: filterable here too.
      apiService.getAiAgents().then((agents) => {
        const extra: Option[] = (agents.data?.agents || []).filter((a) => a.flowId).map((a) => ({ id: a.flowId!, label: `AI agent · ${a.name}`, hint: a.status === 'suspended' || a.paused ? 'Off' : undefined }));
        if (extra.length) setFlows([...own, ...extra]);
      }).catch(() => undefined);
    }).catch(() => undefined);
    if (businessId) {
      apiService.getBusinessUsers(businessId).then((res) => {
        if (res.statusCode === 1000 && Array.isArray(res.data)) {
          setUsers(res.data.map((u) => ({ id: u.userId || u.user?._id, label: [u.user?.firstName, u.user?.lastName].filter(Boolean).join(' ') || u.user?.email || u.userId, hint: u.isAiAgent || u.role === 'ai_agent' ? 'AI agent' : u.user?.email })));
        }
      }).catch(() => undefined);
      apiService.getDepartments(businessId).then((res) => {
        if (res.statusCode === 1000 && Array.isArray(res.data)) setDepartments(res.data.map((d: any) => ({ id: String(d._id || d.id), label: d.name })));
      }).catch(() => undefined);
    }
  }, [businessId]);

  const filters = useMemo(() => {
    const days = RANGES.find((r) => r.id === range)?.days;
    const from = days ? new Date(Date.now() - (days === 1 ? new Date().getHours() * 3_600_000 + new Date().getMinutes() * 60_000 : days * 86_400_000)).toISOString() : undefined;
    return { flowId, userId, departmentId, contact: contact || undefined, ref: ref || undefined, from };
  }, [flowId, userId, departmentId, contact, ref, range]);

  const load = useCallback(async (skip: number) => {
    const id = ++request.current;
    if (skip === 0) setLoading(true); else setLoadingMore(true);
    try {
      if (tab === 'skipped') {
        const res = await apiService.getFlowRunSkips({ flowId: filters.flowId, contact: filters.contact, from: filters.from, limit: LIMIT, skip });
        if (id !== request.current) return;
        const items = res.data?.items ?? [];
        setSkips((prev) => (skip ? [...prev, ...items] : items));
        setTotal(res.data?.total ?? 0);
      } else {
        const res = await apiService.getFlowRuns({ ...filters, status: tab, limit: LIMIT, skip });
        if (id !== request.current) return;
        const items = res.data?.items ?? [];
        setRuns((prev) => (skip ? [...prev, ...items] : items));
        setTotal(res.data?.total ?? 0);
      }
    } finally {
      if (id === request.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [tab, filters]);

  useEffect(() => {
    setExpandedId(null);
    load(0);
  }, [load, reloadKey]);

  const shown = tab === 'skipped' ? skips.length : runs.length;
  const loadMore = () => {
    if (!loading && !loadingMore && shown < total) load(shown);
  };

  const anyFilter = !!(flowId || (tab !== 'skipped' && (userId || departmentId || ref)) || contact || range !== 'any');
  const clearFilters = () => {
    setFlowId(undefined);
    setUserId(undefined);
    setDepartmentId(undefined);
    setContact('');
    setRef('');
    setRange('any');
  };

  const act = async (run: { id: string }, action: 'resume' | 'stop' | 'move', nodeId?: string) => {
    try {
      const res = action === 'resume' ? await apiService.resumeFlowSession(run.id)
        : action === 'move' ? await apiService.moveFlowSession(run.id, nodeId!)
          : await apiService.stopFlowSession(run.id);
      if (res.statusCode !== 1000) throw new Error((res.errors as any)?.[0]?.message || (res.errors as any)?.[0] || 'Action failed');
      const continuesLater = action === 'resume' && (res.data as any)?.status === 'paused';
      showNotification('success', action === 'resume' ? 'Resumed' : action === 'move' ? 'Moved' : 'Stopped',
        action === 'stop' ? 'The flow stopped for this contact.' : continuesLater ? "It continues on the contact's next message." : 'The flow is continuing in the conversation.');
      setReloadKey((k) => k + 1);
      // The run goes on in the background: look again once it has moved.
      if (action !== 'stop') setTimeout(() => setReloadKey((k) => k + 1), 2500);
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Action failed');
    }
  };

  const tabLabel = TABS.find((x) => x.id === tab)?.label.toLowerCase();

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId: string) => {
        const { setPendingSettingsItem } = await import('../../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
    >
    <View style={styles.root}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('flows-list')} activeOpacity={0.7}>
          <DirectionalIcon name="chevron-back" size={20} color={colors.text.primary as string} />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>Flow runs</Text>
          <Text style={styles.headerSubtitle}>{total} {tab === 'all' ? 'runs' : tab === 'skipped' ? 'skipped starts (last 7 days)' : `${tabLabel} runs`}</Text>
        </View>
        <TouchableOpacity accessibilityLabel="Refresh" onPress={() => setReloadKey((k) => k + 1)} style={styles.backButton}>
          <Ionicons name="refresh" size={16} color={colors.text.primary as string} />
        </TouchableOpacity>
      </View>

      <View style={styles.tabs}>
        {TABS.map((item) => (
          <TouchableOpacity key={item.id} accessibilityRole="tab" accessibilityState={{ selected: tab === item.id }}
            style={[styles.tab, tab === item.id && styles.tabActive]} onPress={() => setTab(item.id)}>
            <Text style={[styles.tabText, tab === item.id && styles.tabTextActive]}>{item.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.filters}>
        <SelectFilter icon="git-network-outline" placeholder="All flows" value={flowId} options={flows} onChange={setFlowId} />
        <SearchFilter icon="person-outline" placeholder="Contact name or phone" value={contact} onChange={setContact} />
        {tab !== 'skipped' && <SelectFilter icon="people-outline" placeholder="Any team member / AI agent" value={userId} options={users} onChange={setUserId} />}
        {tab !== 'skipped' && <SelectFilter icon="business-outline" placeholder="Any department" value={departmentId} options={departments} onChange={setDepartmentId} />}
        {tab !== 'skipped' && <SearchFilter icon="pricetag-outline" placeholder="Any id: record, task, event…" value={ref} onChange={setRef} width={220} />}
        <View style={styles.rangeRow}>
          {RANGES.map((r) => (
            <TouchableOpacity key={r.id} style={[styles.rangeChip, range === r.id && styles.rangeChipOn]} onPress={() => setRange(r.id)}>
              <Text style={[styles.rangeText, range === r.id && styles.rangeTextOn]}>{r.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {anyFilter && (
          <TouchableOpacity onPress={clearFilters} style={styles.clearButton}>
            <Text style={styles.link}>Clear filters</Text>
          </TouchableOpacity>
        )}
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary as string} />
        </View>
      ) : tab === 'skipped' ? (
        <FlatList
          data={skips}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          onEndReached={loadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={<Empty text="No skipped starts. When an attached flow does not start for a message (turned off, not published, or the contact is already in it), it shows here." />}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ margin: 12 }} color={colors.primary as string} /> : null}
          renderItem={({ item }) => (
            <View style={styles.runCard}>
              <View style={styles.row}>
                <Ionicons name="remove-circle-outline" size={20} color={colors.warning as string} />
                <View style={styles.flex}>
                  <Text style={styles.runTitle} numberOfLines={1}>{item.flowName || 'Flow'} · {SKIP_REASON[item.reason] || item.reason}</Text>
                  <Text style={styles.runMeta} numberOfLines={2}>
                    {item.contactName || `Contact ${String(item.contactId || '').slice(-6)}`} · {TRIGGER[item.scope] || item.scope} · {formatDate(item.createdAt)}
                    {item.text ? ` · "${item.text}"` : ''}
                  </Text>
                </View>
                {!!item.conversationId && (
                  <TouchableOpacity style={styles.smallButton} onPress={() => navigateTo('conversation', item.conversationId)}>
                    <Ionicons name="chatbubbles-outline" size={14} color={colors.primary as string} />
                    <Text style={styles.smallButtonText}>Conversation</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}
        />
      ) : (
        <FlatList
          data={runs}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          onEndReached={loadMore}
          onEndReachedThreshold={0.3}
          ListEmptyComponent={<Empty text={anyFilter ? 'No runs match these filters.' : 'No runs yet. Every time a flow starts for a contact, the run shows here.'} />}
          ListFooterComponent={loadingMore ? <ActivityIndicator style={{ margin: 12 }} color={colors.primary as string} /> : null}
          renderItem={({ item }) => (
            <RunRow
              run={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
              canEdit={canEdit}
              onAction={act}
              onFilterRef={(id) => { setTab('all'); setRef(id); }}
              onOpenConversation={(id) => navigateTo('conversation', id)}
              onOpenFlow={(id, runId) => navigateTo('flow-edit', id, { runId })}
            />
          )}
        />
      )}
    </View>
    </ScreenLayout>
  );
};

const Empty: React.FC<{ text: string }> = ({ text }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.empty}>
      <Ionicons name="git-branch-outline" size={26} color={colors.text.secondary as string} />
      <Text style={styles.emptyText}>{text}</Text>
    </View>
  );
};

const RunRow: React.FC<{
  run: FlowRunItem;
  expanded: boolean;
  onToggle: () => void;
  canEdit: boolean;
  onAction: (run: { id: string }, action: 'resume' | 'stop' | 'move', nodeId?: string) => void;
  onFilterRef: (id: string) => void;
  onOpenConversation: (id: string) => void;
  onOpenFlow: (id: string, runId?: string) => void;
}> = ({ run, expanded, onToggle, canEdit, onAction, onFilterRef, onOpenConversation, onOpenFlow }) => {
  const styles = useThemedStyles(getStyles);
  const meta = statusMeta(run.status);
  const counts = Object.entries(run.effectCounts || {}) as Array<[FlowRunEffect['kind'], number]>;
  const duration = (run.endedAt ? new Date(run.endedAt).getTime() : new Date(run.lastAdvancedAt).getTime()) - new Date(run.startedAt).getTime();
  return (
    <View style={[styles.runCard, expanded && styles.runCardOpen]}>
      <TouchableOpacity onPress={onToggle} style={styles.row} activeOpacity={0.7}>
        <AppIcon name={(run.errorCount > 0 && run.status === 'completed' ? 'alert-circle' : meta.icon)} size={22} color={run.errorCount > 0 ? (colors.error as string) : meta.color} />
        <View style={styles.flex}>
          <View style={styles.titleRow}>
            <Text style={styles.runTitle} numberOfLines={1}>{run.flowName}</Text>
            <View style={[styles.badge, { backgroundColor: withAlpha(meta.color, 0.12) }]}>
              <Text style={[styles.badgeText, { color: meta.color }]}>{meta.label}</Text>
            </View>
            {run.errorCount > 0 && (
              <View style={[styles.badge, { backgroundColor: withAlpha(colors.error, 0.12) }]}>
                <Text style={[styles.badgeText, { color: colors.error as string }]}>{run.errorCount} error{run.errorCount > 1 ? 's' : ''}</Text>
              </View>
            )}
          </View>
          <Text style={styles.runMeta} numberOfLines={1}>
            <Ionicons name={(CHANNEL_ICON[run.channel] || 'chatbubble-outline') as any} size={11} color={colors.text.secondary as string} />{' '}
            {run.contactName || run.contactPhone || `Contact ${run.contactId.slice(-6)}`}
            {run.trigger ? ` · ${TRIGGER[run.trigger.scope] || run.trigger.scope}${run.trigger.label ? `: ${run.trigger.label}` : ''}` : ''}
            {` · ${formatDate(run.startedAt)}`}
            {duration > 0 ? ` · ${formatDuration(duration)}` : ''}
          </Text>
          <Text style={styles.runMeta} numberOfLines={1}>
            {run.stepCount} step{run.stepCount === 1 ? '' : 's'}
            {counts.map(([kind, n]) => ` · ${n} ${(EFFECT_KIND[kind]?.label || kind).toLowerCase()}`).join('')}
            {run.status === 'waiting' && run.waitUntil ? ` · waiting until ${formatDate(run.waitUntil)}` : ''}
            {run.status !== 'completed' && run.status !== 'stopped' && run.lastStep ? ` · at ${run.lastStep.nodeName || run.lastStep.nodeType}` : ''}
            {run.endedReason ? ` · ${ENDED_REASON[run.endedReason] || run.endedReason}` : ''}
          </Text>
        </View>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>
      {!!(run.participants?.users.length || run.participants?.departments.length) && (
        <View style={[styles.row, { flexWrap: 'wrap', marginTop: 6 }]}>
          {run.participants?.users.map((u) => <TouchableOpacity key={`user:${u.id}`} onPress={() => onFilterRef(`user:${u.id}`)} style={styles.smallButton}>
            <Text style={styles.smallButtonText}>{u.name || u.id}</Text>
          </TouchableOpacity>)}
          {run.participants?.departments.map((d) => <TouchableOpacity key={`department:${d.id}`} onPress={() => onFilterRef(`department:${d.id}`)} style={styles.smallButton}>
            <Text style={styles.smallButtonText}>{d.name || d.id}</Text>
          </TouchableOpacity>)}
        </View>
      )}
      {expanded && (
        <RunDetailView runId={run.id} canEdit={canEdit} onAction={onAction} onFilterRef={onFilterRef} onOpenConversation={onOpenConversation} onOpenFlow={onOpenFlow} />
      )}
    </View>
  );
};

const RECORD_PAGE = 30;

const RunDetailView: React.FC<{
  runId: string;
  canEdit: boolean;
  onAction: (run: { id: string }, action: 'resume' | 'stop' | 'move', nodeId?: string) => void;
  onFilterRef: (id: string) => void;
  onOpenConversation: (id: string) => void;
  onOpenFlow: (id: string, runId?: string) => void;
}> = ({ runId, canEdit, onAction, onFilterRef, onOpenConversation, onOpenFlow }) => {
  const [picking, setPicking] = useState(false);
  const styles = useThemedStyles(getStyles);
  const [run, setRun] = useState<FlowRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [effectFilter, setEffectFilter] = useState<FlowRunEffect['kind'] | 'all'>('all');
  const [effectLimit, setEffectLimit] = useState(RECORD_PAGE);
  const [showValues, setShowValues] = useState(false);

  useEffect(() => {
    let alive = true;
    apiService.getFlowRun(runId).then((res) => {
      if (!alive) return;
      if (res.statusCode === 1000 && res.data) setRun(res.data);
      else setError((res.errors as any)?.[0]?.message || 'Could not load this run');
    }).catch((e) => alive && setError(e?.message || 'Could not load this run'));
    return () => { alive = false; };
  }, [runId]);

  if (error) return <Text style={[styles.hint, { color: colors.error as string, marginTop: 8 }]}>{error}</Text>;
  if (!run) return <ActivityIndicator style={{ marginTop: 12 }} color={colors.primary as string} />;

  const kinds = Array.from(new Set(run.effects.map((e) => e.kind)));
  const effects = (effectFilter === 'all' ? run.effects : run.effects.filter((e) => e.kind === effectFilter)).slice().reverse();
  const nodeName = (id: string) => run.steps.find((s) => s.nodeId === id)?.nodeName;
  const live = run.status === 'active' || run.status === 'waiting' || run.status === 'paused';

  return (
    <View style={styles.details}>
      {/* Summary + actions */}
      <View style={styles.infoGrid}>
        <Info label="Contact" value={[run.contactName, run.contactPhone].filter(Boolean).join(' · ') || run.contactId} />
        <Info label="Started" value={formatDate(run.startedAt)} />
        <Info label={run.endedAt ? 'Ended' : 'Last step'} value={formatDate(run.endedAt || run.lastAdvancedAt)} />
        <Info label="Trigger" value={run.trigger ? `${TRIGGER[run.trigger.scope] || run.trigger.scope}${run.trigger.label ? `: ${run.trigger.label}` : ''}` : '—'} />
        {!!run.participants?.users.length && <Info label="People / AI agents" value={run.participants.users.map((u) => u.name || u.id).join(', ')} />}
        {!!run.participants?.departments.length && <Info label="Departments" value={run.participants.departments.map((d) => d.name || d.id).join(', ')} />}
        {!!run.trigger?.text && <Info label="First message" value={`"${run.trigger.text}"`} />}
        <Info label="Flow version" value={run.versionNumber !== undefined ? `v${run.versionNumber}` : 'draft'} />
        {live && <Info label="Now at" value={run.currentNode?.name || run.currentNode?.id || '—'} />}
        {run.status === 'waiting' && !!run.waitUntil && <Info label="Waiting until" value={formatDate(run.waitUntil)} />}
        {!!run.stoppedBy && <Info label="Stopped by" value={run.stoppedBy.name || run.stoppedBy.id} />}
        {!!run.resumedBy && <Info label="Resumed by" value={run.resumedBy.name || run.resumedBy.id} />}
      </View>
      <View style={styles.actionsRow}>
        {!!run.conversationId && (
          <TouchableOpacity style={styles.smallButton} onPress={() => onOpenConversation(run.conversationId!)}>
            <Ionicons name="chatbubbles-outline" size={14} color={colors.primary as string} />
            <Text style={styles.smallButtonText}>Open conversation</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={styles.smallButton} onPress={() => onOpenFlow(run.flowId, run.id)}>
          <Ionicons name="git-network-outline" size={14} color={colors.primary as string} />
          <Text style={styles.smallButtonText}>Show path on the flow</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.smallButton} onPress={() => onFilterRef(run.contactId)}>
          <Ionicons name="person-outline" size={14} color={colors.primary as string} />
          <Text style={styles.smallButtonText}>All runs for this contact</Text>
        </TouchableOpacity>
        {canEdit && !!run.conversationId && !!run.nodes?.length && (
          <TouchableOpacity style={styles.smallButton} onPress={() => setPicking(true)}>
            <Ionicons name="play-skip-forward-outline" size={14} color={colors.primary as string} />
            <Text style={styles.smallButtonText}>Continue from a step…</Text>
          </TouchableOpacity>
        )}
        <FlowRunStepPicker
          visible={picking}
          nodes={run.nodes || []}
          currentNodeId={live ? run.currentNode?.id : undefined}
          onClose={() => setPicking(false)}
          onPick={(nodeId) => { setPicking(false); onAction(run, 'move', nodeId); }}
        />
        {canEdit && run.status === 'paused' && (
          <TouchableOpacity style={[styles.smallButton, styles.primaryButton]} onPress={() => onAction(run, 'resume')}>
            <Ionicons name="play" size={14} color="white" />
            <Text style={[styles.smallButtonText, { color: 'white' }]}>Resume</Text>
          </TouchableOpacity>
        )}
        {canEdit && live && (
          <TouchableOpacity style={styles.smallButton} onPress={() => onAction(run, 'stop')}>
            <Ionicons name="stop" size={14} color={colors.error as string} />
            <Text style={[styles.smallButtonText, { color: colors.error as string }]}>Stop</Text>
          </TouchableOpacity>
        )}
      </View>
      {(run.parent || run.children.length > 0) && (
        <View style={styles.actionsRow}>
          {run.parent && (
            <TouchableOpacity style={styles.linkChip} onPress={() => onFilterRef(run.parent!.id)}>
              <Text style={styles.link}>Called from: {run.parent.flowName || 'flow'} ({run.parent.status})</Text>
            </TouchableOpacity>
          )}
          {run.children.map((c) => (
            <TouchableOpacity key={c.id} style={styles.linkChip} onPress={() => onFilterRef(c.id)}>
              <Text style={styles.link}>Called: {c.flowName || 'flow'} ({c.status})</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Counts */}
      {kinds.length > 0 && (
        <View style={styles.countsGrid}>
          <Count label="Steps" value={run.steps.length} />
          {kinds.map((k) => <Count key={k} label={EFFECT_KIND[k]?.label || k} value={run.effects.filter((e) => e.kind === k).length} />)}
          {run.nodeErrors.length > 0 && <Count label="Errors" value={run.nodeErrors.length} tone="error" />}
        </View>
      )}

      {/* Timeline */}
      <Text style={styles.subTitle}>Steps</Text>
      {run.steps.length === 0 ? (
        <Text style={styles.hint}>No steps recorded. Runs from before step tracking was added only show their status.</Text>
      ) : (
        <View style={styles.timeline}>
          {run.steps.map((step, i) => {
            const o = outcomeMeta(step.outcome);
            const type = NODE_TYPE_META.find((m) => m.type === step.nodeType);
            return (
              <View key={i} style={styles.stepRow}>
                <View style={styles.stepRail}>
                  <View style={[styles.stepDot, { borderColor: type?.color || (colors.border as string) }]}>
                    <Ionicons name={(type?.icon || 'ellipse-outline') as any} size={12} color={type?.color || (colors.text.secondary as string)} />
                  </View>
                  {i < run.steps.length - 1 && <View style={styles.stepLine} />}
                </View>
                <View style={styles.flex}>
                  <View style={styles.titleRow}>
                    <Text style={styles.stepTitle} numberOfLines={1}>{step.nodeName || type?.label || step.nodeType}</Text>
                    <Ionicons name={o.icon as any} size={13} color={o.color} />
                    {!!step.path && (
                      <View style={styles.pathChip}><Text style={styles.pathText}>→ {step.path}</Text></View>
                    )}
                    <Text style={styles.stepTime}>{formatTime(step.at)}{step.ms ? ` · ${formatDuration(step.ms)}` : ''}</Text>
                  </View>
                  {!!step.detail && (
                    <Text selectable style={[styles.stepDetail, step.outcome === 'error' && { color: colors.error as string }]}>{localizeDetail(step.detail)}</Text>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      )}

      {/* Affected records */}
      {run.effects.length > 0 && (
        <View style={styles.block}>
          <Text style={styles.subTitle}>Affected records</Text>
          <View style={styles.chips}>
            {(['all', ...kinds] as Array<FlowRunEffect['kind'] | 'all'>).map((k) => (
              <TouchableOpacity key={k} onPress={() => { setEffectFilter(k); setEffectLimit(RECORD_PAGE); }} style={[styles.chip, effectFilter === k && styles.chipActive]}>
                <Text style={[styles.chipText, effectFilter === k && styles.chipTextActive]}>{k === 'all' ? 'All' : EFFECT_KIND[k]?.label || k}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {effects.slice(0, effectLimit).map((e, i) => (
            <View key={i} style={styles.recordRow}>
              <Text style={[styles.actionTag, actionTone(e.action)]}>{e.action}</Text>
              <Ionicons name={(EFFECT_KIND[e.kind]?.icon || 'ellipse-outline') as any} size={14} color={colors.text.secondary as string} />
              <View style={styles.flex}>
                <Text selectable style={styles.listPrimary} numberOfLines={2}>
                  {e.label || EFFECT_KIND[e.kind]?.label || e.kind}
                  {e.listName ? ` · ${e.listName}` : ''}
                </Text>
                <Text selectable style={styles.listSecondary} numberOfLines={1}>
                  {[
                    nodeName(e.nodeId),
                    ...(e.users || []).map((u) => u.name || u.id),
                    ...(e.departments || []).map((d) => d.name || d.id),
                    e.refId ? `#${e.refId}` : '',
                    formatTime(e.at),
                  ].filter(Boolean).join(' · ')}
                </Text>
              </View>
              {!!e.refId && (
                <TouchableOpacity accessibilityLabel="Runs that touched this record" hitSlop={8} onPress={() => onFilterRef(e.refId!)}>
                  <Ionicons name="filter-outline" size={14} color={colors.primary as string} />
                </TouchableOpacity>
              )}
            </View>
          ))}
          {effects.length > effectLimit && (
            <TouchableOpacity onPress={() => setEffectLimit(effectLimit + RECORD_PAGE)}>
              <Text style={styles.link}>Show {Math.min(RECORD_PAGE, effects.length - effectLimit)} more</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Errors */}
      {run.nodeErrors.length > 0 && (
        <View style={styles.errorBox}>
          <Text style={[styles.subTitle, { color: colors.error as string }]}>Errors</Text>
          {run.nodeErrors.slice().reverse().map((err, i) => (
            <Text key={i} selectable style={styles.mono}>{formatDate(err.at)} · {err.nodeName || err.nodeType}: {err.message}</Text>
          ))}
        </View>
      )}

      {/* Saved values */}
      <TouchableOpacity onPress={() => setShowValues(!showValues)} style={{ marginTop: 12 }}>
        <Text style={styles.link}>{showValues ? 'Hide saved values' : 'Show saved values (variables)'}</Text>
      </TouchableOpacity>
      {showValues && (
        <View style={styles.logBox}>
          <Text selectable style={styles.mono}>{JSON.stringify(run.variables, null, 2)}</Text>
        </View>
      )}
    </View>
  );
};

const Info: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.infoCell}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text selectable style={styles.infoValue} numberOfLines={3}>{value}</Text>
    </View>
  );
};

const Count: React.FC<{ label: string; value: number; tone?: 'error' }> = ({ label, value, tone }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.countCell}>
      <Text style={[styles.countValue, tone === 'error' && value > 0 && { color: colors.error as string }]}>{value}</Text>
      <Text style={styles.countLabel}>{label}</Text>
    </View>
  );
};

const actionTone = (action: FlowRunEffect['action']) => {
  const color = action === 'created' || action === 'booked' ? (colors.status.success as string)
    : action === 'deleted' ? (colors.error as string)
      : action === 'updated' ? (colors.warning as string)
        : (colors.primary as string);
  return { color, backgroundColor: withAlpha(color, 0.1) };
};

const MONO = Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' });

const getStyles = () => StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.background.base },
  flex: { flex: 1, minWidth: 0 },
  row: { flexDirection: 'row', alignItems: 'center', gap: designTokens.spacing.sm },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  backButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerTitle: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  headerSubtitle: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  listContent: { padding: designTokens.spacing.md, gap: designTokens.spacing.sm },
  tabs: { flexDirection: 'row', flexWrap: 'wrap', gap: designTokens.spacing.xs, paddingHorizontal: designTokens.spacing.md, paddingTop: designTokens.spacing.sm },
  tab: { paddingHorizontal: designTokens.spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.full, borderWidth: 1, borderColor: colors.border },
  tabActive: { backgroundColor: colors.brand.primary, borderColor: colors.brand.primary },
  tabText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  tabTextActive: { color: colors.white },
  filters: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: designTokens.spacing.xs, paddingHorizontal: designTokens.spacing.md, paddingTop: designTokens.spacing.sm },
  filterPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 10,
    maxWidth: 260,
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  filterPillOn: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary, 0.06) },
  filterPillText: { flexShrink: 1, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  filterPillTextOn: { color: colors.primary },
  filterInput: { flex: 1, minWidth: 0, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.primary, color: colors.text.primary, paddingVertical: 0, outlineStyle: 'none' as any },
  rangeRow: { flexDirection: 'row', borderRadius: designTokens.borderRadius.full, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' },
  rangeChip: { paddingHorizontal: 10, height: 30, justifyContent: 'center' },
  rangeChipOn: { backgroundColor: withAlpha(colors.primary, 0.1) },
  rangeText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  rangeTextOn: { color: colors.primary },
  clearButton: { paddingHorizontal: 6 },
  empty: { alignItems: 'center', gap: 8, padding: designTokens.spacing.xl },
  emptyText: { textAlign: 'center', fontSize: typography.fontSize.sm, color: colors.text.secondary, maxWidth: 420 },
  runCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    padding: designTokens.spacing.sm,
  },
  runCardOpen: { borderColor: colors.primary },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  runTitle: { flexShrink: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  runMeta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
  badge: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8 },
  badgeText: { fontSize: 10, fontFamily: typography.fontFamily.semibold },
  details: { marginTop: designTokens.spacing.sm, paddingTop: designTokens.spacing.sm, borderTopWidth: 1, borderTopColor: colors.border },
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: designTokens.spacing.sm },
  infoCell: { minWidth: 160, flexGrow: 1, flexBasis: 160 },
  infoLabel: { fontSize: 10, fontFamily: typography.fontFamily.medium, color: colors.text.secondary, textTransform: 'uppercase' },
  infoValue: { fontSize: typography.fontSize.sm, color: colors.text.primary, marginTop: 1 },
  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: designTokens.spacing.sm },
  smallButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  primaryButton: { backgroundColor: colors.brand.primary, borderColor: colors.brand.primary },
  smallButtonText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  linkChip: { paddingVertical: 4 },
  link: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.primary },
  countsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: designTokens.spacing.md },
  countCell: { minWidth: 84, flexGrow: 1, alignItems: 'center', paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.background.secondary },
  countValue: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  countLabel: { fontSize: 10, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  subTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary, marginTop: designTokens.spacing.md, marginBottom: 6 },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  timeline: { gap: 0 },
  stepRow: { flexDirection: 'row', gap: 10 },
  stepRail: { width: 24, alignItems: 'center' },
  stepDot: { width: 24, height: 24, borderRadius: 12, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.surface },
  stepLine: { flex: 1, width: 1.5, minHeight: 10, backgroundColor: colors.border },
  stepTitle: { flexShrink: 1, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  stepTime: { fontSize: 10, color: colors.text.secondary, marginLeft: 'auto' },
  stepDetail: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1, marginBottom: 10 },
  pathChip: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8, backgroundColor: withAlpha(colors.primary, 0.1) },
  pathText: { fontSize: 10, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  block: { marginTop: designTokens.spacing.xs },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 6 },
  chip: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 14, borderWidth: 1, borderColor: colors.border },
  chipActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary, 0.08) },
  chipText: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
  chipTextActive: { color: colors.primary },
  recordRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  actionTag: { fontSize: 10, fontFamily: typography.fontFamily.semibold, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, overflow: 'hidden', minWidth: 64, textAlign: 'center', textTransform: 'capitalize' },
  listPrimary: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  listSecondary: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 1 },
  errorBox: { marginTop: designTokens.spacing.md, padding: designTokens.spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: withAlpha(colors.error, 0.07), gap: 2 },
  mono: { fontFamily: MONO, fontSize: 11, color: colors.text.primary },
  logBox: { marginTop: 6, padding: designTokens.spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.background.secondary },
  modalOverlay: overlayPickerStyles().overlay,
  modalCard: { ...overlayPickerStyles().card, padding: designTokens.spacing.md },
  modalTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  searchInput: { height: 36, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, paddingHorizontal: 10, color: colors.text.primary, fontSize: typography.fontSize.sm },
  optionRow: { paddingVertical: 9, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  optionText: { fontSize: typography.fontSize.sm, color: colors.text.primary },
  optionHint: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
});
